import { useClient } from "../../api/ClientContext";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { routeHref } from "../../app/routing";
import { useAsync } from "../../stores/useAsync";
import { useSession } from "../../auth/SessionProvider";

/**
 * The projects this identity may see.
 *
 * Filtered by MEMBERSHIP on the server (§7.3). A project the user has no
 * membership of is not listed and not addressable — hiding it in the UI would
 * be decoration, so the server does not return it at all.
 */
export function ProjectListPage(): JSX.Element {
  const client = useClient();
  const { session, roleOn } = useSession();
  const projects = useAsync(() => client.listProjects(), [client]);

  return (
    <div className="page">
      <header className="page-head">
        <h1>Projects</h1>
        <p className="muted">
          Signed in as {session.identity?.displayName ?? session.identity?.subject}
          {session.identity?.roles.length ? ` · ${session.identity.roles.join(", ")}` : ""}
        </p>
      </header>

      <ErrorBanner error={projects.error} onDismiss={projects.reload} />

      <Panel title="Available">
        {projects.loading ? <Empty>Loading…</Empty>
          : !projects.data?.length ? <Empty>You are not a member of any project on this deployment.</Empty>
          : (
            <ul className="project-list">
              {projects.data.map(project => (
                <li key={project.projectId}>
                  <a href={routeHref({ name: "project", projectId: project.projectId })}>
                    <strong>{project.title}</strong>
                    <span className="muted">{project.projectId}</span>
                  </a>
                  <span className="muted">
                    {roleOn(project.projectId) ?? "no membership"} · canonical {project.canonicalRevision.slice(0, 12)}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </Panel>
    </div>
  );
}
